import { useMemo } from 'react';

import { Alert, FlexAligner, Typography } from '@scaleaq/scaleui';
import { StatusLevel } from '@scaleaq/sitecontrol-lib';
import { useTranslation } from 'react-i18next';

import { InfoGroupCard, InfoGroupColumnCount, type InfoGroupEntry } from '@/components';
import { asNonI18n } from '@/localization';
import { typeLabelForDevice } from '@/providers';
import type { DeviceSummary } from '@/types';
import { Icon, responsive } from '@/utils';
import { colors, statusLevelToIndicatorType } from '@/utils';

/** Use for sibling `InfoGroupCard`s in the same device info modal so column count matches the overview. */
export const deviceInfoOverviewGridColumns = 2 as const;

const overviewHeaderIcon = (
  <Icon.Info
    size='large'
    aria-hidden
    style={{
      width: '1.25rem',
      height: '1.25rem',
      color: colors.icon.info[100],
    }}
  />
);

type DeviceInfoOverviewProps = {
  device: DeviceSummary;
  /** Rows after name, type, parent, and status (e.g. tank or silo index). */
  extraEntries?: ReadonlyArray<InfoGroupEntry>;
  columns?: InfoGroupColumnCount;
};

export const DeviceInfoOverview = ({
  device,
  extraEntries,
  columns = deviceInfoOverviewGridColumns,
}: DeviceInfoOverviewProps) => {
  const { t } = useTranslation(['deviceConfig']);

  const entries = useMemo((): InfoGroupEntry[] => {
    const list: InfoGroupEntry[] = [
      {
        id: 'name',
        label: t('deviceConfig:name'),
        value: device.name,
        tooltip: (
          <FlexAligner direction={'column'} gap={responsive.spacing.xsmall}>
            <Typography>{device.name}</Typography>
            <Typography>{`(${t('deviceConfig:camera.doubleClickToCopy')})`}</Typography>
          </FlexAligner>
        ),
        copyable: true,
      },
      { id: 'type', label: t('deviceConfig:type'), value: typeLabelForDevice(device) },
    ];
    if (device.location != null) {
      list.push({
        id: 'parent',
        label: t('deviceConfig:deviceForm.parent'),
        value: device.location,
      });
    }
    if (device.status != null) {
      list.push({
        id: 'status',
        label: t('deviceConfig:status'),
        value: asNonI18n(device.status.message),
        tooltip: asNonI18n(device.status.message),
      });
    }
    return extraEntries != null && extraEntries.length > 0 ? [...list, ...extraEntries] : list;
  }, [device, extraEntries, t]);

  const overviewTitle = t('deviceConfig:infoModal.overview');

  return (
    <>
      {device.status && device.status.level !== StatusLevel.Healthy && (
        <Alert
          text={`${device.status.level}: ${device.status.message}`}
          severity={statusLevelToIndicatorType(device.status.level)}
          style={{ width: '100%' }}
        ></Alert>
      )}
      <InfoGroupCard
        header={{ title: overviewTitle, icon: overviewHeaderIcon }}
        entries={entries}
        columns={columns}
      />
    </>
  );
};
